import { subscribeBoardRefreshHints } from "./use-board.js";

// WHY [BOL-CLEAR-CHAT-001]: Goal 2 item 3. "Clear chat" records a boundary on the
// server (one per member, so it holds on every device) and the Chat shows only the
// turns after it; earlier turns stay in the ledger behind "Show earlier messages".
// Two pure rules live here so they can be tested without a browser: how the
// boundary is read from the history payload, and that a cleared feed never
// resurrects turns cached in this browser from before the clear.
export function opsFeedClearedAt(history: unknown): string | null {
  if (!history || typeof history !== "object") return null;
  const value = (history as { clearedAt?: unknown }).clearedAt;
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function feedCacheTurns<T>(
  cache: readonly T[], clearedAt: string | null, cachedClearedAt?: string | null
): readonly T[] {
  // WHY [BOL-CLEAR-CHAT-PRESENTATION-002]: treating any historical clear as
  // permanent cache invalidation erased NEW spoken greetings/progress on every
  // reload. Admit continuity only under the same authenticated feed boundary.
  // Never use client clocks to decide which speech predates another device's
  // clear; an unbound old cache is safe only when the server has no clear.
  return (cachedClearedAt ?? null) === clearedAt ? cache : [];
}

export function opsHistoryPageCursor(history: unknown): string | null {
  if (!history || typeof history !== "object") return null;
  const cursor = (history as { nextCursor?: unknown }).nextCursor;
  return typeof cursor === "string" && cursor.length > 0 && cursor.length <= 512 ? cursor : null;
}

// Refresh is shared with mounted-event synchronization. Stop cancels this
// dispatch's wait, not that other owner's authenticated background read.
export async function awaitOpsHistoryRefresh(refresh: () => Promise<boolean>, signal: AbortSignal): Promise<boolean> {
  signal.throwIfAborted();
  let abort!: () => void;
  const interrupted = new Promise<never>((_resolve, reject) => {
    abort = () => reject(signal.reason);
    signal.addEventListener("abort", abort, { once: true });
  });
  try { return await Promise.race([refresh(), interrupted]); }
  finally { signal.removeEventListener("abort", abort); }
}

// A clear retires completed conversation, not the locally active request or its
// eventual terminal result. In particular it is not Stop and must not abort an
// upload, Voice turn or approved effect. A draft is owned separately by the UI.
export function pendingTurnsAfterFeedClear<T extends { id: string; response: unknown; cancelled?: boolean | undefined }>(
  turns: readonly T[], activeTurnId?: string
): T[] {
  return turns.filter((turn) => turn.id === activeTurnId || (turn.response === null && !turn.cancelled));
}

// WHY [BOL-OPS-HISTORY-MOUNTED-001]: one mount-only GET missed scheduled turns
// and another device's clear. Subscribe to the already-open authenticated SSE,
// and re-anchor on reconnect/focus/foreground after a sleeping mobile tab. Hints
// carry no history authority; the existing scoped GET still owns every row.
export function connectOpsHistoryRefresh(
  scope: string,
  refresh: () => void,
  browser: { window: Pick<Window, "addEventListener" | "removeEventListener">;
    document: Pick<Document, "addEventListener" | "removeEventListener" | "visibilityState"> }
): () => void {
  const [workspaceId, memberId] = scope.split(":");
  const unsubscribe = subscribeBoardRefreshHints((hint) => {
    if (hint.workspaceId !== workspaceId) return;
    if (hint.kind === "READY" || hint.event === "ops.history.changed"
      || (hint.event === "ops.feed.cleared" && hint.entityId === memberId)) refresh();
  });
  const onVisible = () => { if (browser.document.visibilityState === "visible") refresh(); };
  browser.window.addEventListener("focus", onVisible);
  browser.document.addEventListener("visibilitychange", onVisible);
  return () => {
    unsubscribe();
    browser.window.removeEventListener("focus", onVisible);
    browser.document.removeEventListener("visibilitychange", onVisible);
  };
}
